import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Wallet, Loader2, ShoppingCart, CreditCard, ArrowDownLeft,
  ArrowUpRight, AlertTriangle, Tag, RotateCcw, RefreshCw, Calendar,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
const fmtTime = (iso) => (iso || "").slice(11, 16);
const fmtDay = (iso) => (iso || "").slice(0, 10);

// Map activity_log entries → financial event metadata.
// Returns null for non-financial entries so we can filter them out.
const classifyEvent = (e) => {
  const a = e.action || "";
  const r = e.resource_type || "";
  const meta = e.meta || {};
  if (r === "order" && a === "create") {
    const amount = Number(meta.total || 0);
    const isSup = !!meta.is_supervisor_sale;
    const isDirect = !!meta.is_direct_sale;
    return {
      icon: ShoppingCart,
      color: isSup ? "#F59E0B" : isDirect ? "#3B82F6" : "#10B981",
      bg: isSup ? "#FEF3C7" : isDirect ? "#E0F2FE" : "#ECFDF5",
      label: isSup ? "بيع مشرف" : isDirect ? "بيع مباشر" : "طلب زبون",
      amount, dir: "up", // adds debt
      subtitle: `+${fmt(amount)} د.ع دين`,
    };
  }
  if (r === "supervisor_invoice" && a === "create") {
    const amount = Number(meta.total || 0);
    return {
      icon: ShoppingCart, color: "#F59E0B", bg: "#FEF3C7",
      label: "فاتورة مشرف", amount, dir: "up",
      subtitle: `+${fmt(amount)} د.ع دين`,
    };
  }
  if (r === "receipt" || r === "payment_voucher" || a === "receipt_create") {
    const amount = Number(meta.amount || 0);
    return {
      icon: CreditCard, color: "#10B981", bg: "#ECFDF5",
      label: "سند قبض", amount, dir: "down",
      subtitle: `−${fmt(amount)} د.ع من الدين`,
    };
  }
  if (r === "order" && a === "discount") {
    const amount = Number(meta.discount || 0);
    return {
      icon: Tag, color: "#7C3AED", bg: "#F5F3FF",
      label: "خصم مبلغ", amount, dir: "down",
      subtitle: `−${fmt(amount)} د.ع من الدين والربح`,
    };
  }
  if (r === "order" && (a === "delete" || a === "reject")) {
    return {
      icon: RotateCcw, color: "#E85D45", bg: "#FEF2F2",
      label: a === "delete" ? "حذف فاتورة" : "رفض فاتورة",
      amount: 0, dir: "down",
      subtitle: "ارتجاع المخزن + إلغاء الدين",
    };
  }
  if (r === "damaged_item" && a === "create") {
    const amount = Number(meta.total_cost || 0);
    return {
      icon: AlertTriangle, color: "#DC2626", bg: "#FEE2E2",
      label: "تلف مواد", amount, dir: "down",
      subtitle: `−${fmt(amount)} د.ع خسارة من الربح`,
    };
  }
  if (r === "supplier_voucher" || a === "supplier_payment") {
    const amount = Number(meta.amount || 0);
    return {
      icon: ArrowUpRight, color: "#0891B2", bg: "#CFFAFE",
      label: "دفع لمُجهّز", amount, dir: "down",
      subtitle: `−${fmt(amount)} د.ع من الصندوق`,
    };
  }
  if (r === "cash" || a === "deposit_capital") {
    const amount = Number(meta.amount || 0);
    return {
      icon: ArrowDownLeft, color: "#22C55E", bg: "#ECFDF5",
      label: "إيداع رأس مال", amount, dir: "up",
      subtitle: `+${fmt(amount)} د.ع للصندوق`,
    };
  }
  if (r === "order_return" || (r === "return" && a === "approve")) {
    return {
      icon: RotateCcw, color: "#F59E0B", bg: "#FEF3C7",
      label: "موافقة على إرجاع", amount: 0, dir: "down",
      subtitle: meta.total_amount ? `−${fmt(meta.total_amount)} د.ع` : "إرجاع للمخزن",
    };
  }
  return null;
};

const RANGES = [
  { key: "today", label: "اليوم", hours: 24 },
  { key: "3d", label: "3 أيام", hours: 72 },
  { key: "7d", label: "أسبوع", hours: 168 },
  { key: "30d", label: "30 يوم", hours: 720 },
];

export default function FinancialActivityTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("today");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/activity-log`, { params: { limit: 500 } });
      setItems(data.items || []);
    } catch { toast.error("فشل التحميل"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Filter + classify
  const filtered = useMemo(() => {
    const r = RANGES.find((x) => x.key === range);
    const cutoff = r ? new Date(Date.now() - r.hours * 3600 * 1000).toISOString() : null;
    return items
      .filter((e) => !cutoff || (e.created_at || "") >= cutoff)
      .map((e) => ({ ...e, _event: classifyEvent(e) }))
      .filter((e) => e._event);
  }, [items, range]);

  // Compute net totals
  const totals = useMemo(() => {
    let inflow = 0, outflow = 0;
    filtered.forEach((e) => {
      const ev = e._event;
      if (!ev || !ev.amount) return;
      if (ev.dir === "up") inflow += ev.amount;
      else outflow += ev.amount;
    });
    return { inflow, outflow, net: inflow - outflow };
  }, [filtered]);

  // Group by day
  const grouped = useMemo(() => {
    const out = {};
    filtered.forEach((e) => {
      const day = fmtDay(e.created_at);
      out[day] = out[day] || [];
      out[day].push(e);
    });
    return out;
  }, [filtered]);

  const dayKeys = Object.keys(grouped).sort().reverse();

  return (
    <div className="space-y-3" dir="rtl" data-testid="financial-activity-tab">
      {/* Header */}
      <div className="bg-gradient-to-l from-[#15803D] to-[#22C55E] text-white rounded-2xl p-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Wallet className="w-6 h-6" />
            <div>
              <h3 className="font-bold text-lg">الحركة المالية اليومية</h3>
              <p className="text-xs opacity-90">كل صفقة مالية بترتيب زمني — قصّ، قبض، خصم، تلف، إرجاع</p>
            </div>
          </div>
          <button onClick={load} data-testid="fa-refresh" className="bg-white/20 hover:bg-white/30 rounded-full p-2">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Range filter */}
      <div className="bg-white rounded-2xl p-3 border-2 border-[#E4EBE6] flex flex-wrap gap-1.5" data-testid="fa-ranges">
        {RANGES.map((r) => (
          <button
            key={r.key}
            onClick={() => setRange(r.key)}
            data-testid={`fa-range-${r.key}`}
            className={`text-xs font-bold py-1.5 px-3 rounded-full transition-colors ${
              range === r.key ? "bg-[#15803D] text-white" : "bg-[#F3F6F4] text-[#5C6E64] hover:bg-[#E4EBE6]"
            }`}
          >{r.label}</button>
        ))}
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-white rounded-2xl p-3 border-2 border-[#E4EBE6] text-center" data-testid="fa-inflow">
          <ArrowDownLeft className="w-5 h-5 mx-auto text-[#15803D] mb-1" />
          <p className="text-[10px] text-[#5C6E64]">دين مضاف (+)</p>
          <p className="text-lg font-black text-[#15803D]" dir="ltr">{fmt(totals.inflow)}</p>
        </div>
        <div className="bg-white rounded-2xl p-3 border-2 border-[#E4EBE6] text-center" data-testid="fa-outflow">
          <ArrowUpRight className="w-5 h-5 mx-auto text-[#E85D45] mb-1" />
          <p className="text-[10px] text-[#5C6E64]">دين منقوص (−)</p>
          <p className="text-lg font-black text-[#E85D45]" dir="ltr">{fmt(totals.outflow)}</p>
        </div>
        <div className="bg-white rounded-2xl p-3 border-2 border-[#E4EBE6] text-center" data-testid="fa-net">
          <Wallet className="w-5 h-5 mx-auto text-[#1E3A5F] mb-1" />
          <p className="text-[10px] text-[#5C6E64]">الصافي</p>
          <p className={`text-lg font-black ${totals.net >= 0 ? "text-[#15803D]" : "text-[#E85D45]"}`} dir="ltr">
            {totals.net >= 0 ? "+" : ""}{fmt(totals.net)}
          </p>
        </div>
      </div>

      {/* Timeline */}
      {loading ? (
        <div className="text-center py-10"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
      ) : dayKeys.length === 0 ? (
        <div className="bg-white border-2 border-dashed rounded-2xl p-10 text-center text-[#5C6E64]" data-testid="fa-empty">
          <Wallet className="w-10 h-10 mx-auto opacity-30 mb-2" />
          لا توجد حركة مالية بهذه الفترة
        </div>
      ) : (
        dayKeys.map((day) => (
          <div key={day}>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-xs font-bold text-[#1E3A5F] bg-[#E0F2FE] px-3 py-0.5 rounded-full inline-flex items-center gap-1" dir="ltr">
                <Calendar className="w-3 h-3" /> {day}
              </span>
              <span className="text-[10px] text-[#5C6E64]">({grouped[day].length} حركة)</span>
            </div>
            <div className="space-y-1.5">
              {grouped[day].map((e) => {
                const ev = e._event;
                const Icon = ev.icon;
                return (
                  <div
                    key={e.id}
                    data-testid={`fa-row-${e.id}`}
                    className="bg-white rounded-xl p-2.5 flex items-center gap-2.5 border border-[#E4EBE6] hover:border-[#3B82F6]/40 transition-colors"
                  >
                    <div className="rounded-full p-2 shrink-0" style={{ backgroundColor: ev.bg }}>
                      <Icon className="w-4 h-4" style={{ color: ev.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-[#111814] leading-tight truncate">{e.summary || "—"}</p>
                      <div className="flex items-center gap-2 mt-0.5 text-[10px] text-[#5C6E64]">
                        <span className="px-1.5 py-0.5 rounded font-bold" style={{ backgroundColor: ev.bg, color: ev.color }}>
                          {ev.label}
                        </span>
                        <span>{e.actor_name || "—"}</span>
                        <span dir="ltr" className="ml-auto font-bold tabular-nums">{fmtTime(e.created_at)}</span>
                      </div>
                    </div>
                    {ev.amount > 0 && (
                      <div
                        className="text-left shrink-0"
                        style={{ color: ev.dir === "up" ? "#15803D" : "#E85D45" }}
                      >
                        <p className="text-[10px] opacity-70">{ev.dir === "up" ? "+" : "−"}</p>
                        <p className="font-black tabular-nums text-sm" dir="ltr">{fmt(ev.amount)}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
